import { User } from 'oidc-client-ts';
import { createApiClient } from '../oidc-api';
import { ChargeDto, PaymentDto, PaginatedResponsePaymentDto } from './types';
import { withRetry } from '../applications/applicationsService';

/**
 * Get all charges for a specific user
 */
export const getUserCharges = async (
  user: User | null,
  userId: string
): Promise<ChargeDto[]> => {
  const api = createApiClient(user);
  return withRetry(() => api.get<ChargeDto[]>(`/v1/users/${userId}/charges`));
};

/**
 * Get all payments for a specific user
 * This method combines data from getUserCharges and then fetches payments for each charge
 */
export const getUserPayments = async (
  user: User | null,
  userId: string,
  pageNumber: number = 1,
  pageSize: number = 10
): Promise<PaginatedResponsePaymentDto> => {
  // Remove unused api variable declaration
  // const api = createApiClient(user);

  try {
    // First, get all charges for the user
    const charges = await getUserCharges(user, userId);

    if (!charges || charges.length === 0) {
      return {
        content: [],
        current_page: pageNumber,
        page_size: pageSize,
        total_elements: 0
      };
    }

    // Then get payments for each charge
    const allPaymentsPromises = charges.map(charge =>
      charge.id ? getPaymentsByChargeId(user, charge.id) : Promise.resolve([])
    );

    const allPaymentsArrays = await Promise.all(allPaymentsPromises);

    // Flatten the array of arrays and add charge info to each payment
    const allPayments = allPaymentsArrays.reduce((acc, payments, index) => {
      const charge = charges[index];
      const paymentsWithCharge = payments.map(payment => ({
        ...payment,
        // Add charge data for reference
        charge: charge
      }));
      return [...acc, ...paymentsWithCharge];
    }, [] as (PaymentDto & { charge?: ChargeDto })[]);

    // Sort payments by created_at date (newest first)
    allPayments.sort((a, b) => {
      const dateA = a.created_at ? new Date(a.created_at).getTime() : 0;
      const dateB = b.created_at ? new Date(b.created_at).getTime() : 0;
      return dateB - dateA;
    });

    // Paginate the results manually
    const startIndex = (pageNumber - 1) * pageSize;
    const paginatedPayments = allPayments.slice(startIndex, startIndex + pageSize);

    return {
      content: paginatedPayments,
      current_page: pageNumber,
      page_size: pageSize,
      total_elements: allPayments.length
    };
  } catch (error) {
    console.error('Error fetching user payments:', error);
    throw error;
  }
};

/**
 * Get a specific charge by ID
 */
export const getChargeById = async (
  user: User | null,
  chargeId: string
): Promise<ChargeDto> => {
  const api = createApiClient(user);
  return withRetry(() => api.get<ChargeDto>(`/v1/charges/${chargeId}`));
};

/**
 * Get all payments for a specific charge
 */
export const getPaymentsByChargeId = async (
  user: User | null,
  chargeId: string
): Promise<PaymentDto[]> => {
  const api = createApiClient(user);
  return withRetry(() => api.get<PaymentDto[]>(`/v1/charges/${chargeId}/payments`));
};

/**
 * Get a specific payment by ID for a specific charge
 */
export const getPaymentByChargeIdAndPaymentId = async (
  user: User | null,
  chargeId: string,
  paymentId: string
): Promise<PaymentDto> => {
  const api = createApiClient(user);
  return withRetry(() => api.get<PaymentDto>(`/v1/charges/${chargeId}/payments/${paymentId}`));
};

/**
 * Create new charges
 * Note: This is a state-changing operation, so we don't use retry to avoid duplicate charges
 */
export const createCharges = async (
  user: User | null,
  charges: ChargeDto[]
): Promise<ChargeDto[]> => {
  const api = createApiClient(user);
  return api.post<ChargeDto[]>('/v1/charges', charges);
};